import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, BarChart, PieChart } from './_finHelpers';

// IRR — fixed cash flow OR irregular cash flow
export default function IRRCalc() {
  const [mode, setMode] = useState('fixed');
  return (
    <CalcShell look="cnet" title="Internal Rate of Return (IRR) Calculator" subtitle="Compute IRR for a series of fixed or irregular cash flows."
      about={<><p>The <b>IRR</b> is the discount rate that makes the net present value of all cash flows equal to zero. It's often used to compare investment opportunities.</p></>}
    >
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        <button onClick={()=>setMode('fixed')} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${mode==='fixed'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`} data-testid="irr-tab-fixed">Fixed Cash Flow</button>
        <button onClick={()=>setMode('irregular')} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${mode==='irregular'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`} data-testid="irr-tab-irregular">Irregular Cash Flow</button>
      </div>
      {mode === 'fixed' ? <FixedFlow/> : <IrregularFlow/>}
    </CalcShell>
  );
}

function solveIRR(flows) {
  let lo = -0.99, hi = 5;
  for (let i = 0; i < 100; i++) {
    const mid = (lo + hi) / 2;
    const npv = flows.reduce((acc, cf, t) => acc + cf / Math.pow(1 + mid, t), 0);
    if (npv > 0) lo = mid; else hi = mid;
  }
  return ((lo + hi) / 2) * 100;
}

function ResultView({ flows, irr }) {
  const totalIn = flows.slice(1).reduce((a,b)=>a+b, 0);
  const initial = -flows[0];
  const netProfit = totalIn - initial;
  const npv0 = flows.reduce((a,cf,t)=>a+cf,0);
  return (
    <div data-print="tables">
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Cash Flow Timeline</h3>
        <BarChart
          data={flows.map((f, i) => ({
            label: i === 0 ? 'Init' : String(i),
            values: [{ value: Math.abs(f), color: f < 0 ? '#ef4444' : '#10b981', label: f < 0 ? 'Outflow' : 'Inflow' }],
          }))}
          stack={true}
          legend={[
            { label: 'Investment (outflow)', color: '#ef4444' },
            { label: 'Return (inflow)',      color: '#10b981' },
          ]}
        />
      </div>
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Cash Flow Detail</h3>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm" data-testid="irr-schedule-table">
            <thead className="bg-blue-800 text-white sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">Year</th>
                <th className="px-3 py-2 text-right">Cash Flow</th>
                <th className="px-3 py-2 text-right">Cumulative</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let cum = 0;
                return flows.map((f, i) => {
                  cum += f;
                  return (
                    <tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}>
                      <td className="px-3 py-2">{i === 0 ? 'Initial' : `Year ${i}`}</td>
                      <td className={`px-3 py-2 text-right ${f < 0 ? 'text-red-600' : 'text-green-700'}`}>{fmt(f)}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${cum < 0 ? 'text-red-600' : 'text-green-700'}`}>{fmt(cum)}</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FixedFlow() {
  const [initial, setInitial] = useState(30000);
  const [flow, setFlow] = useState(8000);
  const [years, setYears] = useState(5);
  const c = useMemo(() => {
    const flows = [-initial, ...Array(years).fill(flow)];
    return { flows, irr: solveIRR(flows), totalIn: flow * years, netProfit: flow * years - initial };
  }, [initial, flow, years]);
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div data-print="inputs">
          <div className="field"><label>Initial investment</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={initial} onChange={e=>setInitial(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Annual cash flow</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={flow} onChange={e=>setFlow(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Number of years</label><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/></div>
        </div>
        <div data-print="result">
          <div className="result-panel mb-4"><div className="result-title">Internal Rate of Return</div><div className="result-value" data-testid="irr-fixed">{c.irr.toFixed(3)}%</div></div>
          <table className="data-table"><tbody>
            <tr><td>Initial Investment</td><td className="text-right text-red-600">-{fmt(initial)}</td></tr>
            <tr><td>Total Cash Received</td><td className="text-right text-green-700">{fmt(c.totalIn)}</td></tr>
            <tr><td className="font-semibold">Net Profit</td><td className={`text-right font-bold ${c.netProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmt(c.netProfit)}</td></tr>
          </tbody></table>
          <div className="mt-4">
            <PieChart parts={[
              { label: 'Investment',     value: initial,      color: '#ef4444' },
              { label: 'Net Profit',     value: Math.max(0, c.netProfit),  color: '#10b981' },
            ]} size={170}/>
          </div>
        </div>
      </div>
      <ResultView flows={c.flows} irr={c.irr}/>
    </>
  );
}

function IrregularFlow() {
  const [initial, setInitial] = useState(30000);
  const [flows, setFlows] = useState([5000, 7000, 9000, 12000, 14000]);
  const update = (i, v) => setFlows(prev => prev.map((f, idx) => idx === i ? v : f));
  const addYear = () => setFlows(prev => [...prev, 0]);
  const c = useMemo(() => {
    const allFlows = [-initial, ...flows];
    const totalIn = flows.reduce((a,b)=>a+b, 0);
    return { flows: allFlows, irr: solveIRR(allFlows), totalIn, netProfit: totalIn - initial };
  }, [initial, flows]);
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div data-print="inputs">
          <div className="field mb-3"><label>Initial investment</label><div className="flex gap-2 max-w-xs"><span className="self-center text-slate-500">$</span><input type="number" value={initial} onChange={e=>setInitial(+e.target.value||0)} className="flex-1"/></div></div>
          <table className="w-full text-sm mb-4">
            <thead className="bg-slate-100"><tr><th className="px-2 py-2 text-left">Year</th><th className="px-2 py-2 text-right">Cash flow</th></tr></thead>
            <tbody>
              {flows.map((f, i) => (
                <tr key={i} className="border-b"><td className="px-2 py-2">Year {i+1}</td><td className="px-2 py-2"><div className="flex gap-1"><span>$</span><input type="number" value={f} onChange={e=>update(i, +e.target.value||0)} className="!py-1 flex-1"/></div></td></tr>
              ))}
            </tbody>
          </table>
          <button onClick={addYear} className="text-blue-700 text-sm font-semibold" data-testid="irr-add-year">+ Add another year</button>
        </div>
        <div data-print="result">
          <div className="result-panel mb-4"><div className="result-title">Internal Rate of Return</div><div className="result-value" data-testid="irr-irregular">{c.irr.toFixed(3)}%</div></div>
          <table className="data-table"><tbody>
            <tr><td>Initial Investment</td><td className="text-right text-red-600">-{fmt(initial)}</td></tr>
            <tr><td>Total Cash Received</td><td className="text-right text-green-700">{fmt(c.totalIn)}</td></tr>
            <tr><td className="font-semibold">Net Profit</td><td className={`text-right font-bold ${c.netProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmt(c.netProfit)}</td></tr>
          </tbody></table>
          <div className="mt-4">
            <PieChart parts={[
              { label: 'Investment', value: initial,                    color: '#ef4444' },
              { label: 'Net Profit', value: Math.max(0, c.netProfit),  color: '#10b981' },
            ]} size={170}/>
          </div>
        </div>
      </div>
      <ResultView flows={c.flows} irr={c.irr}/>
    </>
  );
}
